import React, { useState, useMemo } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { R_STRATEGIES } from '../../constants';

const AssetModal = ({ asset, onClose, onSave }) => {
  const isEditing = !!asset;
  const initialAsset = asset || {
    id: `A${Math.floor(Math.random() * 9000 + 1000)}`,
    name: '',
    asset_type: '',
    brand: '',
    model_name: '',
    gtin: '',
    serial: '',
    factory: '',
    date_manufactured: '',
    currency: 'EUR',
    initial_value: 0,
    current_market_value: 0,
    weight_kg: '',
    // dimensions entered via three inputs in the form
    dimensions_length_mm: '',
    dimensions_width_mm: '',
    dimensions_height_mm: '',
    condition_rating: 4.0,
    circularity_score: 70,
    current_strategy: 'R7',
    current_end_of_life_strategy: '',
    last_service: new Date().toISOString().slice(0, 10),
    life_expectancy_in_months: 60,
    age_in_months: 0,
    user_feedback: ''
  };

  const [formData, setFormData] = useState(initialAsset);
  const initialMaterials = [];
  const [materials, setMaterials] = useState([]);
  const [isEstimating, setIsEstimating] = useState(false);

  // Ensure all form values are defined to prevent React controlled input warnings
  const safeFormData = useMemo(() => ({
    id: formData?.id || '',
    name: formData?.name || '',
    asset_type: formData?.asset_type || '',
    brand: formData?.brand || '',
    model_name: formData?.model_name || '',
    gtin: formData?.gtin || '',
    serial: formData?.serial || '',
    factory: formData?.factory || '',
    date_manufactured: formData?.date_manufactured || '',
    currency: formData?.currency || 'EUR',
    initial_value: formData?.initial_value || 0,
    current_market_value: formData?.current_market_value || 0,
    weight_kg: formData?.weight_kg ?? '',
    dimensions_length_mm: formData?.dimensions_length_mm ?? (formData?.dimensions?.length_mm ?? ''),
    dimensions_width_mm: formData?.dimensions_width_mm ?? (formData?.dimensions?.width_mm ?? ''),
    dimensions_height_mm: formData?.dimensions_height_mm ?? (formData?.dimensions?.height_mm ?? ''),
    condition_rating: formData?.condition_rating || 4.0,
    circularity_score: formData?.circularity_score || 70,
    current_strategy: formData?.current_strategy || 'R7',
    current_end_of_life_strategy: formData?.current_end_of_life_strategy || '',
    last_service: formData?.last_service || new Date().toISOString().slice(0, 10),
    life_expectancy_in_months: formData?.life_expectancy_in_months || 60,
    age_in_months: formData?.age_in_months || 0,
    user_feedback: formData?.user_feedback || ''
  }), [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: (
        name === 'condition_rating' ||
        name === 'initial_value' ||
        name === 'current_market_value' ||
        name === 'circularity_score' ||
        name === 'life_expectancy_in_months' ||
        name === 'age_in_months' ||
        name === 'weight_kg'
      ) ? (value === '' ? '' : parseFloat(value)) : value,
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Build dimensions JSON only from provided numeric fields
    const dim = {
      length_mm: safeFormData.dimensions_length_mm === '' ? null : Number(safeFormData.dimensions_length_mm),
      width_mm: safeFormData.dimensions_width_mm === '' ? null : Number(safeFormData.dimensions_width_mm),
      height_mm: safeFormData.dimensions_height_mm === '' ? null : Number(safeFormData.dimensions_height_mm),
    };
    const hasDims = Object.values(dim).some(v => Number.isFinite(v));

    // Build payload aligned with backend Asset schema
    const assetData = {
        id: formData.id,
        name: formData.name,
        asset_type: formData.asset_type,
        brand: formData.brand,
        model_name: formData.model_name,
        gtin: formData.gtin,
        serial: formData.serial,
        factory: formData.factory,
        date_manufactured: formData.date_manufactured || null,
        currency: formData.currency || 'EUR',
        initial_value: formData.initial_value,
        current_market_value: formData.current_market_value,
        weight_kg: formData.weight_kg === '' ? null : formData.weight_kg,
        dimensions: hasDims ? dim : undefined,
        condition_rating: formData.condition_rating,
        circularity_score: formData.circularity_score,
        current_strategy: formData.current_strategy,
        current_end_of_life_strategy: formData.current_end_of_life_strategy || null,
        last_service: formData.last_service || null,
        life_expectancy_in_months: formData.life_expectancy_in_months,
        age_in_months: formData.age_in_months,
        user_feedback: formData.user_feedback || null
      };

    onSave(assetData, isEditing);
    onClose();
  };

  const formFields = [
    // Identity & classification
    { name: 'name', label: 'Asset Name', type: 'text', required: true },
    { name: 'asset_type', label: 'Asset Type', type: 'text', required: true },
    { name: 'brand', label: 'Brand', type: 'text', required: false },
    { name: 'model_name', label: 'Model', type: 'text', required: false },
    { name: 'gtin', label: 'GTIN (14-digit)', type: 'text', required: false },
    { name: 'serial', label: 'Serial', type: 'text', required: false },
    { name: 'factory', label: 'Factory', type: 'text', required: false },
    { name: 'date_manufactured', label: 'Manufactured Date', type: 'date', required: false },

    // Values & currency
    { name: 'currency', label: 'Currency (3-letter)', type: 'text', required: true },
    { name: 'initial_value', label: 'Initial Value', type: 'number', step: '0.01', required: true },
    { name: 'current_market_value', label: 'Current Market Value', type: 'number', step: '0.01', required: true },

    // Physical attributes
    { name: 'weight_kg', label: 'Weight (kg)', type: 'number', step: '0.001', required: false },

    // Lifecycle & condition
    { name: 'life_expectancy_in_months', label: 'Life Expectancy (months)', type: 'number', min: '1', required: true },
    { name: 'age_in_months', label: 'Current Age (months)', type: 'number', min: '0', required: true },
    { name: 'condition_rating', label: 'Condition Rating (0-5)', type: 'range', min: '0', max: '5', step: '0.1', required: true },
    { name: 'circularity_score', label: 'Circularity Score (%)', type: 'range', min: '0', max: '100', step: '1', required: true },
    { name: 'last_service', label: 'Last Service Date', type: 'date', required: false },

    // Strategy/EOL & notes
    { name: 'current_end_of_life_strategy', label: 'Current EOL Strategy', type: 'text', required: false },
    { name: 'user_feedback', label: 'User Feedback', type: 'textarea', required: false },
  ];

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">{isEditing ? 'Edit Asset' : 'Add New Asset'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-gray-500">Asset ID: <span className="font-mono text-gray-700">{safeFormData.id}</span></p>

          {formFields.map(field => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label} {field.type === 'range' && `(${safeFormData[field.name]})`}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  name={field.name}
                  value={safeFormData[field.name]}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
                  rows="3"
                  placeholder="Optional feedback about this asset..."
                />
              ) : (
                <input
                  type={field.type}
                  name={field.name}
                  value={safeFormData[field.name]}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
                  {...(field.type === 'range' ? { min: field.min, max: field.max, step: field.step } : {})}
                />
              )}
            </div>
          ))}

          {/* Dimensions (mm) */}
          <div className="border rounded-lg p-3 bg-white">
            <label className="block text-sm font-semibold text-gray-800 mb-2">Dimensions (mm)</label>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Length</label>
                <input
                  type="number"
                  name="dimensions_length_mm"
                  value={safeFormData.dimensions_length_mm}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
                  min="0"
                  step="0.1"
                  placeholder="e.g., 300"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Width</label>
                <input
                  type="number"
                  name="dimensions_width_mm"
                  value={safeFormData.dimensions_width_mm}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
                  min="0"
                  step="0.1"
                  placeholder="e.g., 200"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Height</label>
                <input
                  type="number"
                  name="dimensions_height_mm"
                  value={safeFormData.dimensions_height_mm}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
                  min="0"
                  step="0.1"
                  placeholder="e.g., 150"
                />
              </div>
            </div>
          </div>

          {/* Materials are now managed separately under Asset Materials. This form reflects the Asset model only. */}
          <div className="border rounded-lg p-3 bg-gray-50 text-gray-700">
            <div className="text-xs">
              Materials are managed via the Asset Materials section and Digital Product Passport. You can add/edit materials later from the asset detail view.
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Strategy</label>
            <select
              name="current_strategy"
              value={safeFormData.current_strategy}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-cyan-500 focus:border-cyan-500"
            >
              {Object.entries(R_STRATEGIES).map(([key, val]) => (
                <option key={key} value={key}>{key} - {val.name}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">Cancel</button>
            <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition flex items-center">
              <CheckCircle className="w-4 h-4 mr-2" /> {isEditing ? 'Save Changes' : 'Add Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssetModal;
